// KAMPÁNY create/edit — jobb oldali beúszó `FormSheet`, KÉT módban.
//
// A KÉT MÓD KÜLÖNBSÉGE EGYETLEN MEZŐ: felvételkor TÖBB fiók jelölhető (fiókonként külön kampány keletkezik),
// szerkesztéskor pontosan egy. A kaszkád (ügyfél → a fiókjai) csak SZŰKÍT: az ügyfelet a szerver deriválja a
// fiókból, a form `clientId`-je nem megy a bodyba.
//
// Az AZONOSÍTÓ csak olvasható: a rendszer generálja, és a sorra hivatkozás kulcsa — egy kézi átírás
// érvénytelenítené a róla szóló e-maileket és jegyzeteket.
import type { CampaignDetail, CampaignRefs } from '@atlas/contracts';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EntityMultiSelect } from '@/components/shared/entity-multiselect';
import {
  ComboboxField,
  DateField,
  SelectField,
  TextField,
  TextareaField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { campaignKeys } from '@/features/campaign-forecast/api';
import {
  buildCampaignCreateResolver,
  buildCampaignUpdateResolver,
  EMPTY_CAMPAIGN_FORM,
  toCampaignCreateBody,
  toCampaignForm,
  toCampaignUpdateBody,
  type CampaignFormValues,
} from '@/features/campaign-forecast/form';
import { api } from '@/lib/api';
import { CURRENCIES } from '@/lib/pricing-form';
import { useEntityMutation } from '@/lib/use-entity-mutation';

export interface CampaignFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` → LÉTREHOZÁS; egyébként a szerkesztendő kampány (OCC `version` + előtöltés). */
  item: CampaignDetail | null;
  refs: CampaignRefs | undefined;
  /**
   * `true` → a form FORRÁSAI nem töltődtek be (üzem-zavar). Enélkül a választók CSENDBEN üresek lennének, és
   * a felhasználó azt hinné, hogy nincs fiók — ezért itt kimondjuk, és a mentést is letiltjuk.
   */
  refsFailed?: boolean;
  /**
   * Törlés indítása a SZERKESZTŐBŐL (a megerősítő dialógust a hívó tartja). Lista-nézet híján ez az
   * egyetlen hely, ahonnan egy kampány törölhető — `undefined`, ha a felhasználónak nincs `delete` joga.
   */
  onDelete?: () => void;
}

export function CampaignFormSheet({
  open,
  onOpenChange,
  item,
  refs,
  refsFailed = false,
  onDelete,
}: CampaignFormSheetProps): JSX.Element {
  const { t } = useTranslation();
  const isEdit = item !== null;

  const form = useForm<CampaignFormValues>({
    resolver: isEdit ? buildCampaignUpdateResolver() : buildCampaignCreateResolver(),
    defaultValues: EMPTY_CAMPAIGN_FORM,
    mode: 'onTouched',
  });
  const { reset, handleSubmit, watch, setValue } = form;

  // A sheet MEGNYITÁSAKOR töltünk (nem rendereléskor): a szerkesztő-példány ugyanaz a komponens, más sorral.
  useEffect(() => {
    if (!open) return;
    reset(item === null ? EMPTY_CAMPAIGN_FORM : toCampaignForm(item));
  }, [open, item, reset]);

  const clientId = watch('clientId');
  const accountIds = watch('accountIds');

  const clientOptions = useMemo(
    () => (refs?.clients ?? []).map((c) => ({ value: c.id, label: c.name })),
    [refs],
  );
  const accountsForClient = useMemo(
    () => (refs?.accounts ?? []).filter((a) => clientId === '' || a.clientId === clientId),
    [refs, clientId],
  );
  const ownerOptions = useMemo(
    () => (refs?.owners ?? []).map((o) => ({ value: o.id, label: o.name })),
    [refs],
  );

  const mutation = useEntityMutation<CampaignFormValues, unknown>({
    mutationFn: (values) =>
      isEdit
        ? api.campaignForecast.update.mutation({
            params: { id: item.id },
            headers: { 'if-match': String(item.version) },
            body: toCampaignUpdateBody(values) as never,
          })
        : api.campaignForecast.create.mutation({
            body: toCampaignCreateBody(values) as never,
          }),
    invalidateKeys: [campaignKeys.all],
    onFieldErrors: (field, messages) => {
      // A szerver `accountIds` mező-hibáját a felvevő mezőre tesszük (ott áll a felhasználó szeme).
      form.setError(field as keyof CampaignFormValues, { message: messages[0] ?? '' });
    },
    messages: {
      success: isEdit ? t('toast:campaignForecast.saved') : t('toast:campaignForecast.created'),
      conflict: t('toast:campaignForecast.conflict'),
      forbidden: t('toast:campaignForecast.forbidden'),
      validation: t('toast:campaignForecast.validation'),
      error: t('toast:campaignForecast.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  });

  const searchIn = (options: { value: string; label: string }[]) => (q: string) =>
    Promise.resolve(
      options
        .filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
        .map((o) => ({ id: o.value, label: o.label })),
    );

  const optionById = (options: { value: string; label: string }[], id: string) => {
    const found = options.find((o) => o.value === id);
    return found ? { id: found.value, label: found.label } : null;
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('campaignForecast.editTitle') : t('campaignForecast.createTitle')}
      description={isEdit ? undefined : t('campaignForecast.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          {/* A törlés a lábléc BAL szélén áll, elkülönítve a mentéstől — véletlen kattintás ellen. */}
          {isEdit && onDelete !== undefined && (
            <Button
              type="button"
              variant="destructive"
              className="mr-auto"
              onClick={onDelete}
              disabled={mutation.isPending}
            >
              {t('campaignForecast.delete')}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={mutation.isPending}
          >
            {t('campaignForecast.cancel')}
          </Button>
          <Button type="submit" disabled={mutation.isPending || refsFailed}>
            {mutation.isPending ? t('campaignForecast.saving') : t('campaignForecast.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <div className="space-y-4">
          {refsFailed && (
            <p
              role="alert"
              className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border px-3 py-2 text-xs"
            >
              {t('campaignForecast.refsUnavailable')}
            </p>
          )}
          {isEdit && (
            <div className="space-y-1.5">
              <Label htmlFor="campaign-code">{t('campaignForecast.fields.code')}</Label>
              <Input id="campaign-code" value={item.code} readOnly disabled />
              <p className="text-subtle text-xs">{t('campaignForecast.fields.codeHint')}</p>
            </div>
          )}

          <ComboboxField<CampaignFormValues>
            name="clientId"
            label={t('campaignForecast.fields.client')}
            hint={t('campaignForecast.fields.clientHint')}
            search={searchIn(clientOptions)}
            initialOption={optionById(clientOptions, clientId)}
          />

          {isEdit ? (
            <ComboboxField<CampaignFormValues>
              name="accountId"
              label={t('campaignForecast.fields.account')}
              required
              search={searchIn(accountsForClient.map((a) => ({ value: a.id, label: a.name })))}
              initialOption={optionById(
                (refs?.accounts ?? []).map((a) => ({ value: a.id, label: a.name })),
                watch('accountId'),
              )}
              clearable={false}
            />
          ) : (
            // A `FormItem`/`FormLabel`/`FormMessage` CSAK `FormField` kontextusban működik (a `useFormField`
            // onnan olvas) — nélküle a lap fehér képernyőre fut. Ez a blokk azért nem a közös
            // `form-fields.tsx` egyik mezője, mert TÖBB értéket tart (tömb), amire nincs közös mező-típus.
            <FormField<CampaignFormValues>
              name="accountIds"
              render={() => (
                <FormItem>
                  <FormLabel>
                    {t('campaignForecast.fields.accounts')}
                    <span className="text-destructive"> *</span>
                  </FormLabel>
                  <EntityMultiSelect
                    options={accountsForClient.map((a) => ({ id: a.id, name: a.name }))}
                    value={accountIds}
                    onChange={(ids) => {
                      setValue('accountIds', ids, { shouldValidate: true, shouldTouch: true });
                    }}
                    aria-label={t('campaignForecast.fields.accounts')}
                  />
                  <p className="text-subtle text-xs">{t('campaignForecast.fields.accountsHint')}</p>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <TextField<CampaignFormValues>
            name="name"
            label={t('campaignForecast.fields.name')}
            required
          />

          <ComboboxField<CampaignFormValues>
            name="ownerId"
            label={t('campaignForecast.fields.owner')}
            required
            search={searchIn(ownerOptions)}
            initialOption={optionById(ownerOptions, watch('ownerId'))}
            clearable={false}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <DateField<CampaignFormValues>
              name="startsOn"
              label={t('campaignForecast.fields.startsOn')}
              required
            />
            <DateField<CampaignFormValues>
              name="endsOn"
              label={t('campaignForecast.fields.endsOn')}
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <TextField<CampaignFormValues>
              name="plannedBudget"
              label={t('campaignForecast.fields.plannedBudget')}
              inputMode="decimal"
            />
            <TextField<CampaignFormValues>
              name="actualBudget"
              label={t('campaignForecast.fields.actualBudget')}
              inputMode="decimal"
            />
            <SelectField<CampaignFormValues>
              name="currency"
              label={t('campaignForecast.fields.currency')}
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
            />
          </div>

          <TextareaField<CampaignFormValues>
            name="description"
            label={t('campaignForecast.fields.description')}
          />

          <TextField<CampaignFormValues>
            name="asanaTaskUrl"
            label={t('campaignForecast.fields.asanaTaskUrl')}
            hint={t('campaignForecast.fields.asanaTaskUrlHint')}
            inputMode="url"
          />
        </div>
      </Form>
    </FormSheet>
  );
}
